/**
 * #285 — where the pin hover card goes, in numbers. The card is drawn at page level with fixed
 * positioning (the map's own box clips whatever its markers draw outside it, and the dashboard
 * map is shorter than the card), so the frame is the viewport and every box is a
 * getBoundingClientRect in viewport coordinates.
 */

export type Placement = 'above' | 'below'
export interface Box { top: number; left: number; width: number; height: number }
export interface Size { width: number; height: number }

/** Space between the pin's box and the card's near edge. The hovered pin image grows by 10 %
 *  from its tip (about 5 px at the head) and the card's arrow protrudes 6 px, so 12 px leaves
 *  the arrow tip clear of the grown head (measured in the browser: 8 px left a 3 px gap). */
export const CARD_GAP = 12
/** The least distance the card keeps from the frame's edge. */
export const CARD_MARGIN = 8
/** How far the arrow stays from the card's sides: inside the 10 px corner radius. */
export const ARROW_INSET = 14

export interface Placed {
  placement: Placement
  /** Sideways slide of the card (px, positive = right) that keeps it inside the frame. */
  shiftX: number
  /** Vertical slide (px, positive = down) when the card fits on neither side of the pin; the card
   *  then overlaps the pin and the arrow is dropped. */
  shiftY: number
  /** The arrow's centre, in px from the card's left edge: under the pin, kept inside the card. */
  arrowX: number
}

export function placeHoverCard({ frame, pin, card }: { frame: Box; pin: Box; card: Size }): Placed {
  const need = card.height + CARD_GAP + CARD_MARGIN
  const roomAbove = pin.top - frame.top
  const roomBelow = frame.top + frame.height - (pin.top + pin.height)
  // Above when it fits, else below when that fits, else the roomier side slid inside the frame.
  const placement: Placement = roomAbove >= need ? 'above' : roomBelow >= need ? 'below' : roomAbove >= roomBelow ? 'above' : 'below'

  // Each axis: where a centred (or gap-spaced) card would start, held inside the frame's margins.
  // The near edge (left, top) wins on a frame smaller than the card, so the start of the card,
  // with the name and status, stays on screen whichever side the card took.
  const centreX = pin.left + pin.width / 2
  const left = centreX - card.width / 2
  const minLeft = frame.left + CARD_MARGIN
  const maxLeft = frame.left + frame.width - CARD_MARGIN - card.width
  const shiftX = Math.max(Math.min(left, maxLeft), minLeft) - left

  const top = placement === 'above' ? pin.top - CARD_GAP - card.height : pin.top + pin.height + CARD_GAP
  const minTop = frame.top + CARD_MARGIN
  const maxTop = frame.top + frame.height - CARD_MARGIN - card.height
  const shiftY = Math.max(Math.min(top, maxTop), minTop) - top

  const arrowX = Math.min(Math.max(card.width / 2 - shiftX, ARROW_INSET), card.width - ARROW_INSET)
  return { placement, shiftX: Math.round(shiftX), shiftY: Math.round(shiftY), arrowX: Math.round(arrowX) }
}
